import type { RadarProperties } from './radarAnalytics';
import { RADAR_LOCALES, radarLocaleFromPath } from '@/i18n/radarLanding';
import { matchMarketingRoute } from '@/marketing/routes';

export type MarketingEvent = 'marketing_page_viewed' | 'marketing_cta_clicked' | 'marketing_example_selected' | 'marketing_inquiry_result' | 'marketing_video_started' | 'marketing_video_completed';
export type LandingEvent = 'radar_landing_viewed' | 'radar_application_clicked' | 'radar_consultation_clicked' | MarketingEvent;
export type LandingPosition = 'header' | 'hero' | 'footer' | 'mobile_sticky' | 'video';
type Transport = {
  start(surface: 'landing'): Promise<void>;
  stop(): void;
  capture(event: LandingEvent, properties: RadarProperties): boolean;
};
const campaignKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'] as const;

/** Campaign codes only: never retain raw URLs, referrers, fbclid or form values. */
export function landingProperties(pathname: string, search: string): RadarProperties | null {
  const route = matchMarketingRoute(pathname);
  const locale = route?.locale ?? radarLocaleFromPath(pathname);
  if (!locale) return null;
  const properties: RadarProperties = {
    landing_path: route?.path ?? RADAR_LOCALES.find(item => item.locale === locale)!.path,
    landing_locale: locale,
  };
  const params = new URLSearchParams(search);
  for (const key of campaignKeys) {
    const values = params.getAll(key);
    if (values.length === 1 && /^[a-zA-Z0-9_-]{1,80}$/.test(values[0])) properties[key] = values[0].toLowerCase();
  }
  return properties;
}

/** One mounted route visit. A short queue preserves clicks made while the SDK loads. */
export function createLandingVisit(pathname: string, search: string, transport: Transport, viewEvent: 'radar_landing_viewed' | 'marketing_page_viewed' = 'radar_landing_viewed') {
  const properties = landingProperties(pathname, search);
  if (properties && viewEvent === 'marketing_page_viewed') properties.page_type = matchMarketingRoute(pathname)?.page;
  let active = false;
  let ready = false;
  let generation = 0;
  let viewed = false;
  let retries = 0;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let pending: { event: LandingEvent; properties: RadarProperties }[] = [];
  // Two retries per active visit; no durable queue or retry loop after departure.
  function retry() {
    ready = false;
    if (retries >= 2) { pending = []; return; }
    retries++;
    retryTimer = setTimeout(() => { retryTimer = undefined; connect(); }, 1000);
  }
  function connect() {
    const current = generation;
    const failed = () => { if (active && generation === current) retry(); };
    try {
      void transport.start('landing').then(() => {
        if (!active || generation !== current) return;
        ready = true; flush();
      }).catch(failed);
    } catch { failed(); }
  }
  function flush() {
    if (!active || !ready) return;
    while (pending.length) {
      const item = pending[0];
      try {
        if (!transport.capture(item.event, item.properties)) { retry(); return; }
      } catch { retry(); return; }
      pending.shift();
      if (item.event === viewEvent) viewed = true;
    }
  }
  function enqueue(event: LandingEvent, extra: RadarProperties = {}) {
    if (!active || !properties || pending.length >= 20) return;
    pending.push({ event, properties: { ...properties, ...extra } });
    flush();
  }
  return {
    start() {
      if (active || !properties) return;
      active = true;
      generation++; retries = 0;
      if (!viewed) enqueue(viewEvent);
      connect();
    },
    stop() {
      active = false; ready = false; generation++; pending = [];
      clearTimeout(retryTimer); retryTimer = undefined;
      transport.stop();
    },
    applicationClicked(position: LandingPosition) {
      enqueue('radar_application_clicked', { cta_position: position });
    },
    consultationClicked(channel: NonNullable<RadarProperties['consultation_channel']>, position: LandingPosition) {
      enqueue('radar_consultation_clicked', { consultation_channel: channel, cta_position: position });
    },
    track(event: MarketingEvent, extra: RadarProperties = {}) { enqueue(event, extra); },
  };
}

export function campaignSearch(pathname: string, search: string): string {
  const properties = landingProperties(pathname, search);
  const query = new URLSearchParams();
  for (const key of campaignKeys) if (properties?.[key]) query.set(key, properties[key]!);
  const serialized = query.toString();
  return serialized ? `?${serialized}` : '';
}
